import React from "react";
import { useTranslation } from "react-i18next";
import {
    Actions,
    Box,
    Button,
    Checkbox,
    Column,
    Columns,
    Divider,
    Drawer,
    Dropdown,
    Stack,
    Text,
    Textarea,
    TextField,
} from "braid-design-system";
import { useToast } from "../../hooks/useToast";
import { ItemGroupResponse } from "../../services/itemGroupService";
import NumericTextField from "../NumericTextField";

export interface ItemFormData {
    item_code: string;
    item_name: string;
    item_webname: string;
    item_group1: string;
    item_uom: string;
    item_case: number | null;
    item_selling: number | null;
    item_onlselling: number | null;
    item_cost: number | null;
    item_avgcost: number | null;
    item_gp: number | null;
    item_fixedgp: number | null;
    item_reordermin: number | null;
    item_reordermax: number | null;
    item_enablereorderalert: boolean;
    item_shortdescription: string;
    item_barcode: string;
    item_qrcode: string;
    item_positem: boolean;
    item_dirgrn: boolean;
    item_hoprice: boolean;
    item_vendorplu: string;
    item_active: boolean;
    item_generagebatch: boolean;
    item_allowstcode: boolean;
    item_calreceipe: boolean;
    item_rawitem: boolean;
    item_activedis: boolean;
    item_weighted: boolean;
    is_returnable: boolean;
}

export interface ItemToEdit extends Partial<ItemFormData> {
    item_code?: string;
}

type NumericFormValue = number | string | null;
type NumericItemFormKey =
    | "item_case"
    | "item_selling"
    | "item_onlselling"
    | "item_cost"
    | "item_avgcost"
    | "item_gp"
    | "item_fixedgp"
    | "item_reordermin"
    | "item_reordermax";

type ItemFormState = Omit<ItemFormData, NumericItemFormKey> & Record<NumericItemFormKey, NumericFormValue>;

interface FormErrors {
    item_code?: string;
    item_name?: string;
    item_webname?: string;
    item_group1?: string;
    item_uom?: string;
    item_case?: string;
    item_selling?: string;
    item_onlselling?: string;
    item_cost?: string;
    item_avgcost?: string;
    item_gp?: string;
    item_fixedgp?: string;
    item_reordermin?: string;
    item_reordermax?: string;
    item_shortdescription?: string;
    item_barcode?: string;
    item_qrcode?: string;
    item_vendorplu?: string;
}

interface CreateUpdateItemDrawerProps {
    isOpen: boolean;
    onClose: () => void;
    itemToEdit?: ItemToEdit;
    itemGroups?: ItemGroupResponse[];
    onSave: (itemData: ItemFormData, editingItem?: ItemToEdit) => Promise<void> | void;
    onItemSaved?: () => void;
    isReadOnly?: boolean;
}

const defaultFormData: ItemFormData = {
    item_code: "",
    item_name: "",
    item_webname: "",
    item_group1: "",
    item_uom: "",
    item_case: null,
    item_selling: null,
    item_onlselling: null,
    item_cost: null,
    item_avgcost: null,
    item_gp: null,
    item_fixedgp: null,
    item_reordermin: null,
    item_reordermax: null,
    item_enablereorderalert: false,
    item_shortdescription: "",
    item_barcode: "",
    item_qrcode: "",
    item_positem: false,
    item_dirgrn: false,
    item_hoprice: false,
    item_vendorplu: "",
    item_active: true,
    item_generagebatch: false,
    item_allowstcode: false,
    item_calreceipe: false,
    item_rawitem: false,
    item_activedis: false,
    item_weighted: false,
    is_returnable: true,
};

const numericFieldKeys: NumericItemFormKey[] = [
    "item_case",
    "item_selling",
    "item_onlselling",
    "item_cost",
    "item_avgcost",
    "item_gp",
    "item_fixedgp",
    "item_reordermin",
    "item_reordermax",
];

const getNumericFieldValue = (value: NumericFormValue): string => {
    if (value === null) {
        return "";
    }

    return String(value);
};

const toNullableNumber = (value: NumericFormValue): number | null => {
    if (value === null) {
        return null;
    }

    const trimmed = String(value).trim();
    return trimmed === "" ? null : Number(trimmed);
};

const calculateItemGp = (selling: NumericFormValue, cost: NumericFormValue): number | null => {
    const sellingNumber = toNullableNumber(selling);
    const costNumber = toNullableNumber(cost);

    if (sellingNumber === null || costNumber === null || sellingNumber === 0) {
        return null;
    }

    return Number((((sellingNumber - costNumber) / sellingNumber) * 100).toFixed(2));
};

const normalizeItemFormData = (formData: ItemFormState): ItemFormData => {
    const normalized = { ...formData } as ItemFormData;

    numericFieldKeys.forEach((key) => {
        normalized[key] = toNullableNumber(formData[key]);
    });

    normalized.item_gp = calculateItemGp(formData.item_selling, formData.item_cost);

    return normalized;
};

const CreateUpdateItemDrawer: React.FC<CreateUpdateItemDrawerProps> = ({
    isOpen,
    onClose,
    itemToEdit,
    itemGroups = [],
    onSave,
    onItemSaved,
    isReadOnly = false,
}) => {
    const { t } = useTranslation();
    const toast = useToast();

    const [formData, setFormData] = React.useState<ItemFormState>(defaultFormData);
    const [errors, setErrors] = React.useState<FormErrors>({});
    const [submitting, setSubmitting] = React.useState(false);

    React.useEffect(() => {
        if (!isOpen) {
            return;
        }

        if (itemToEdit) {
            setFormData({
                ...defaultFormData,
                ...itemToEdit,
                item_case: itemToEdit.item_case ?? null,
                item_selling: itemToEdit.item_selling ?? null,
                item_onlselling: itemToEdit.item_onlselling ?? null,
                item_cost: itemToEdit.item_cost ?? null,
                item_avgcost: itemToEdit.item_avgcost ?? null,
                item_gp: itemToEdit.item_gp ?? null,
                item_fixedgp: itemToEdit.item_fixedgp ?? null,
                item_reordermin: itemToEdit.item_reordermin ?? null,
                item_reordermax: itemToEdit.item_reordermax ?? null,
                item_enablereorderalert: Boolean(itemToEdit.item_enablereorderalert),
                item_positem: Boolean(itemToEdit.item_positem),
                item_dirgrn: Boolean(itemToEdit.item_dirgrn),
                item_hoprice: Boolean(itemToEdit.item_hoprice),
                item_active: itemToEdit.item_active ?? true,
                item_generagebatch: Boolean(itemToEdit.item_generagebatch),
                item_allowstcode: Boolean(itemToEdit.item_allowstcode),
                item_calreceipe: Boolean(itemToEdit.item_calreceipe),
                item_rawitem: Boolean(itemToEdit.item_rawitem),
                item_activedis: Boolean(itemToEdit.item_activedis),
                item_weighted: Boolean(itemToEdit.item_weighted),
                is_returnable: itemToEdit.is_returnable ?? true,
            });
        } else {
            setFormData(defaultFormData);
        }

        setErrors({});
    }, [isOpen, itemToEdit]);

    React.useEffect(() => {
        setFormData((prev) => {
            const itemGp = calculateItemGp(prev.item_selling, prev.item_cost);
            return prev.item_gp === itemGp ? prev : { ...prev, item_gp: itemGp };
        });
    }, [formData.item_selling, formData.item_cost]);

    const updateText = (key: keyof ItemFormData, value: string) => {
        setFormData((prev) => ({ ...prev, [key]: value }));
        if (errors[key as keyof FormErrors]) {
            setErrors((prev) => ({ ...prev, [key]: undefined }));
        }
    };

    const updateNumber = (key: keyof ItemFormData, value: string) => {
        setFormData((prev) => ({ ...prev, [key]: value === "" ? null : value }));
        if (errors[key as keyof FormErrors]) {
            setErrors((prev) => ({ ...prev, [key]: undefined }));
        }
    };

    const flagNumbersOnly = (key: keyof FormErrors) => () => {
        setErrors((prev) => ({
            ...prev,
            [key]: t("page.item_management.drawer.validation.numbers_only", {
                defaultValue: "Please enter numbers only",
            }),
        }));
    };

    const updateBoolean = (key: keyof ItemFormData, value: boolean) => {
        setFormData((prev) => ({ ...prev, [key]: value }));
    };

    // Finished/raw classification is stored as two existing flags
    // (item_calreceipe, item_rawitem) rather than a dedicated column, so this
    // keeps them mutually exclusive from a single selector.
    const itemClassification: "" | "FINISHED" | "RAW" = formData.item_calreceipe
        ? "FINISHED"
        : formData.item_rawitem
            ? "RAW"
            : "";

    const updateClassification = (value: "" | "FINISHED" | "RAW") => {
        setFormData((prev) => ({
            ...prev,
            item_calreceipe: value === "FINISHED",
            item_rawitem: value === "RAW",
        }));
    };

    const validate = () => {
        const nextErrors: FormErrors = {};

        const checkMaxLength = (key: keyof FormErrors, value: string | null | undefined, max: number) => {
            if ((value || "").length > max) {
                nextErrors[key] = t("page.item_management.drawer.validation.max_length", {
                    defaultValue: "Maximum {{count}} characters allowed",
                    count: max,
                });
            }
        };

        if (!formData.item_code.trim()) {
            nextErrors.item_code = t("page.item_management.drawer.validation.required", { defaultValue: "Required" });
        }

        if (!formData.item_name.trim()) {
            nextErrors.item_name = t("page.item_management.drawer.validation.required", { defaultValue: "Required" });
        }

        checkMaxLength("item_code", formData.item_code, 25);
        checkMaxLength("item_name", formData.item_name, 60);
        checkMaxLength("item_webname", formData.item_webname, 40);
        checkMaxLength("item_group1", formData.item_group1, 10);
        checkMaxLength("item_uom", formData.item_uom, 5);
        checkMaxLength("item_shortdescription", formData.item_shortdescription, 500);
        checkMaxLength("item_barcode", formData.item_barcode, 15);
        checkMaxLength("item_qrcode", formData.item_qrcode, 25);
        checkMaxLength("item_vendorplu", formData.item_vendorplu, 30);

        const checkNumeric = (key: keyof FormErrors, value: NumericFormValue, integerOnly = false) => {
            const numericValue = toNullableNumber(value);
            if (numericValue === null) {
                return;
            }

            if (!Number.isFinite(numericValue)) {
                nextErrors[key] = t("page.item_management.drawer.validation.invalid_number", {
                    defaultValue: "Invalid numeric value",
                });
                return;
            }

            if (integerOnly && !Number.isInteger(numericValue)) {
                nextErrors[key] = t("page.item_management.drawer.validation.integer_only", {
                    defaultValue: "Only whole numbers are allowed",
                });
            }
        };

        checkNumeric("item_case", formData.item_case, true);
        checkNumeric("item_selling", formData.item_selling);
        checkNumeric("item_onlselling", formData.item_onlselling);
        checkNumeric("item_cost", formData.item_cost);
        checkNumeric("item_avgcost", formData.item_avgcost);
        checkNumeric("item_gp", formData.item_gp);
        checkNumeric("item_fixedgp", formData.item_fixedgp);
        checkNumeric("item_reordermin", formData.item_reordermin);
        checkNumeric("item_reordermax", formData.item_reordermax);

        setErrors(nextErrors);
        return Object.keys(nextErrors).length === 0;
    };

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        if (isReadOnly) {
            return;
        }

        if (submitting) {
            return;
        }

        if (!validate()) {
            toast.showError(t("page.item_management.drawer.validation.fix_errors", { defaultValue: "Please fix validation errors" }));
            return;
        }

        try {
            setSubmitting(true);
            await onSave(normalizeItemFormData(formData), itemToEdit);

            if (onItemSaved) {
                onItemSaved();
            }

            toast.showSuccess(
                itemToEdit
                    ? t("page.item_management.drawer.success.updated", { defaultValue: "Item updated successfully" })
                    : t("page.item_management.drawer.success.created", { defaultValue: "Item created successfully" }),
            );

            onClose();
        } catch (error: any) {
            toast.showError(error?.message || t("page.item_management.drawer.error.save_failed", { defaultValue: "Failed to save item" }));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Drawer
            open={isOpen}
            onClose={onClose}
            title={
                itemToEdit
                    ? t("page.item_management.drawer.title.update", { defaultValue: "Update Item" })
                    : t("page.item_management.drawer.title.create", { defaultValue: "Create New Item" })
            }
            width="medium"
        >
            <form onSubmit={handleSubmit} autoComplete="off">
                <Box padding="large">
                    <Stack space="large">
                        <Text weight="strong" tone="info">
                            {t("page.item_management.drawer.section.basic", { defaultValue: "Basic Details" })}
                        </Text>
                        <Columns space="small" collapseBelow="desktop">
                            <Column width="1/2">
                                <Stack space="small">
                                    <TextField
                                        label={t("page.item_management.drawer.label.item_code", { defaultValue: "Item Code*" })}
                                        value={formData.item_code}
                                        tone={errors.item_code ? "critical" : undefined}
                                        message={errors.item_code}
                                        onChange={(e) => updateText("item_code", e.currentTarget.value)}
                                    />
                                    <TextField
                                        label={t("page.item_management.drawer.label.item_name", { defaultValue: "Item Name*" })}
                                        value={formData.item_name}
                                        tone={errors.item_name ? "critical" : undefined}
                                        message={errors.item_name}
                                        onChange={(e) => updateText("item_name", e.currentTarget.value)}
                                    />
                                    <TextField
                                        label={t("page.item_management.drawer.label.item_webname", { defaultValue: "Item Web Name" })}
                                        value={formData.item_webname}
                                        tone={errors.item_webname ? "critical" : undefined}
                                        message={errors.item_webname}
                                        onChange={(e) => updateText("item_webname", e.currentTarget.value)}
                                    />
                                    <Box>
                                        <TextField
                                            label={t("page.item_management.drawer.label.item_group1", { defaultValue: "Item Group 1" })}
                                            value={formData.item_group1}
                                            tone={errors.item_group1 ? "critical" : undefined}
                                            message={errors.item_group1}
                                            onChange={(e) => updateText("item_group1", e.currentTarget.value)}
                                            disabled
                                        />
                                        {itemGroups.length > 0 && (
                                            <Box marginTop="xsmall">
                                                <select
                                                    id="item-group1-select"
                                                    aria-label={t("page.item_management.drawer.label.select_item_group", { defaultValue: "Select Item Group" })}
                                                    value={formData.item_group1}
                                                    onChange={(e) => updateText("item_group1", e.currentTarget.value)}
                                                    style={{ width: '100%', padding: '8px', marginTop: '4px' }}
                                                >
                                                    <option value="">{t("page.item_management.drawer.label.select_group", { defaultValue: "-- Select Group --" })}</option>
                                                    {itemGroups.map((group) => (
                                                        <option key={group.group_id} value={group.group_code || ""}>
                                                            {group.group_code} - {group.group_name}
                                                        </option>
                                                    ))}
                                                </select>
                                            </Box>
                                        )}
                                    </Box>
                                    <TextField
                                        label={t("page.item_management.drawer.label.item_uom", { defaultValue: "Item UOM" })}
                                        value={formData.item_uom}
                                        tone={errors.item_uom ? "critical" : undefined}
                                        message={errors.item_uom}
                                        onChange={(e) => updateText("item_uom", e.currentTarget.value)}
                                    />
                                    <Dropdown
                                        id="item-classification"
                                        label={t("page.item_management.drawer.label.item_classification", { defaultValue: "Item Classification" })}
                                        value={itemClassification}
                                        onChange={(e) => updateClassification(e.currentTarget.value as "" | "FINISHED" | "RAW")}
                                    >
                                        <option value="">{t("page.item_management.drawer.option.unclassified", { defaultValue: "-- Not Set --" })}</option>
                                        <option value="FINISHED">{t("page.item_management.drawer.option.finished_product", { defaultValue: "Finished Product" })}</option>
                                        <option value="RAW">{t("page.item_management.drawer.option.raw_material", { defaultValue: "Raw Material" })}</option>
                                    </Dropdown>
                                </Stack>
                            </Column>
                            <Column width="1/2">
                                <Box height="full" display="flex" flexDirection="column" justifyContent="spaceBetween">
                                    <Textarea
                                        label={t("page.item_management.drawer.label.item_shortdescription", { defaultValue: "Description" })}
                                        value={formData.item_shortdescription}
                                        tone={errors.item_shortdescription ? "critical" : undefined}
                                        message={errors.item_shortdescription}
                                        characterLimit={500}
                                        lines={7}
                                        onChange={(e) => updateText("item_shortdescription", e.currentTarget.value)}
                                    />
                                    <Stack space="small">
                                        <TextField
                                            label={t("page.item_management.drawer.label.item_barcode", { defaultValue: "Barcode" })}
                                            value={formData.item_barcode}
                                            tone={errors.item_barcode ? "critical" : undefined}
                                            message={errors.item_barcode}
                                            onChange={(e) => updateText("item_barcode", e.currentTarget.value)}
                                        />
                                        <TextField
                                            label={t("page.item_management.drawer.label.item_qrcode", { defaultValue: "QR Code" })}
                                            value={formData.item_qrcode}
                                            tone={errors.item_qrcode ? "critical" : undefined}
                                            message={errors.item_qrcode}
                                            onChange={(e) => updateText("item_qrcode", e.currentTarget.value)}
                                        />
                                        <TextField
                                            label={t("page.item_management.drawer.label.item_vendorplu", { defaultValue: "Vendor PLU" })}
                                            value={formData.item_vendorplu}
                                            tone={errors.item_vendorplu ? "critical" : undefined}
                                            message={errors.item_vendorplu}
                                            onChange={(e) => updateText("item_vendorplu", e.currentTarget.value)}
                                        />
                                    </Stack>
                                </Box>
                            </Column>
                        </Columns>

                        <Divider />

                        <Text weight="strong" tone="info">
                            {t("page.item_management.drawer.section.pricing", { defaultValue: "Pricing and Reorder" })}
                        </Text>
                        <Columns space="small" collapseBelow="desktop">
                            <Column width="1/2">
                                <Stack space="small">
                                    <NumericTextField
                                        label={t("page.item_management.drawer.label.item_case", { defaultValue: "Item Case" })}
                                        value={getNumericFieldValue(formData.item_case)}
                                        mode="integer"
                                        tone={errors.item_case ? "critical" : undefined}
                                        message={errors.item_case}
                                        onChange={(value) => updateNumber("item_case", value)}
                                        onInvalid={flagNumbersOnly("item_case")}
                                    />
                                    <NumericTextField
                                        label={t("page.item_management.drawer.label.item_selling", { defaultValue: "Item Selling" })}
                                        value={getNumericFieldValue(formData.item_selling)}
                                        tone={errors.item_selling ? "critical" : undefined}
                                        message={errors.item_selling}
                                        onChange={(value) => updateNumber("item_selling", value)}
                                        onInvalid={flagNumbersOnly("item_selling")}
                                    />
                                    <NumericTextField
                                        label={t("page.item_management.drawer.label.item_onlselling", { defaultValue: "Online Selling" })}
                                        value={getNumericFieldValue(formData.item_onlselling)}
                                        tone={errors.item_onlselling ? "critical" : undefined}
                                        message={errors.item_onlselling}
                                        onChange={(value) => updateNumber("item_onlselling", value)}
                                        onInvalid={flagNumbersOnly("item_onlselling")}
                                    />
                                    <NumericTextField
                                        label={t("page.item_management.drawer.label.item_cost", { defaultValue: "Item Cost" })}
                                        value={getNumericFieldValue(formData.item_cost)}
                                        tone={errors.item_cost ? "critical" : undefined}
                                        message={errors.item_cost}
                                        onChange={(value) => updateNumber("item_cost", value)}
                                        onInvalid={flagNumbersOnly("item_cost")}
                                    />
                                    <NumericTextField
                                        label={t("page.item_management.drawer.label.item_avgcost", { defaultValue: "Item Avg Cost" })}
                                        value={getNumericFieldValue(formData.item_avgcost)}
                                        disabled
                                        tone={errors.item_avgcost ? "critical" : undefined}
                                        message={errors.item_avgcost}
                                        onChange={(value) => updateNumber("item_avgcost", value)}
                                        onInvalid={flagNumbersOnly("item_avgcost")}
                                    />
                                </Stack>
                            </Column>
                            <Column width="1/2">
                                <Stack space="small">
                                    <NumericTextField
                                        label={t("page.item_management.drawer.label.item_gp", { defaultValue: "Item GP" })}
                                        value={getNumericFieldValue(formData.item_gp)}
                                        disabled
                                        allowNegative
                                        tone={errors.item_gp ? "critical" : undefined}
                                        message={errors.item_gp}
                                        onChange={(value) => updateNumber("item_gp", value)}
                                        onInvalid={flagNumbersOnly("item_gp")}
                                    />
                                    <NumericTextField
                                        label={t("page.item_management.drawer.label.item_fixedgp", { defaultValue: "Item Fixed GP" })}
                                        value={getNumericFieldValue(formData.item_fixedgp)}
                                        tone={errors.item_fixedgp ? "critical" : undefined}
                                        message={errors.item_fixedgp}
                                        onChange={(value) => updateNumber("item_fixedgp", value)}
                                        onInvalid={flagNumbersOnly("item_fixedgp")}
                                    />
                                    <NumericTextField
                                        label={t("page.item_management.drawer.label.item_reordermin", { defaultValue: "Reorder Point" })}
                                        value={getNumericFieldValue(formData.item_reordermin)}
                                        mode="integer"
                                        tone={errors.item_reordermin ? "critical" : undefined}
                                        message={errors.item_reordermin}
                                        onChange={(value) => updateNumber("item_reordermin", value)}
                                        onInvalid={flagNumbersOnly("item_reordermin")}
                                    />
                                    <NumericTextField
                                        label={t("page.item_management.drawer.label.item_reordermax", { defaultValue: "Reorder Quantity" })}
                                        value={getNumericFieldValue(formData.item_reordermax)}
                                        mode="integer"
                                        tone={errors.item_reordermax ? "critical" : undefined}
                                        message={errors.item_reordermax}
                                        onChange={(value) => updateNumber("item_reordermax", value)}
                                        onInvalid={flagNumbersOnly("item_reordermax")}
                                    />
                                </Stack>
                            </Column>
                        </Columns>

                        <Divider />

                        <Text weight="strong" tone="info">
                            {t("page.item_management.drawer.section.flags", { defaultValue: "Flags" })}
                        </Text>
                        <Columns space="small" collapseBelow="desktop">
                            <Column width="1/2">
                                <Stack space="small">
                                    <Checkbox
                                        label={t("page.item_management.drawer.flag.item_enablereorderalert", { defaultValue: "Enable Reorder Alert" })}
                                        checked={formData.item_enablereorderalert}
                                        onChange={(e) => updateBoolean("item_enablereorderalert", e.currentTarget.checked)}
                                    />
                                    <Checkbox
                                        label={t("page.item_management.drawer.flag.item_positem", { defaultValue: "POS Item" })}
                                        checked={formData.item_positem}
                                        onChange={(e) => updateBoolean("item_positem", e.currentTarget.checked)}
                                    />
                                    <Checkbox
                                        label={t("page.item_management.drawer.flag.item_dirgrn", { defaultValue: "Direct GRN" })}
                                        checked={formData.item_dirgrn}
                                        onChange={(e) => updateBoolean("item_dirgrn", e.currentTarget.checked)}
                                    />
                                    <Checkbox
                                        label={t("page.item_management.drawer.flag.item_hoprice", { defaultValue: "HO Price" })}
                                        checked={formData.item_hoprice}
                                        onChange={(e) => updateBoolean("item_hoprice", e.currentTarget.checked)}
                                    />
                                    <Checkbox
                                        label={t("page.item_management.drawer.flag.item_active", { defaultValue: "Active" })}
                                        checked={formData.item_active}
                                        onChange={(e) => updateBoolean("item_active", e.currentTarget.checked)}
                                    />
                                    <Checkbox
                                        label={t("page.item_management.drawer.flag.item_generagebatch", { defaultValue: "Generate Batch" })}
                                        checked={formData.item_generagebatch}
                                        onChange={(e) => updateBoolean("item_generagebatch", e.currentTarget.checked)}
                                    />
                                </Stack>
                            </Column>
                            <Column width="1/2">
                                <Stack space="small">
                                    <Checkbox
                                        label={t("page.item_management.drawer.flag.item_allowstcode", { defaultValue: "Allow ST Code" })}
                                        checked={formData.item_allowstcode}
                                        onChange={(e) => updateBoolean("item_allowstcode", e.currentTarget.checked)}
                                    />
                                    <Checkbox
                                        label={t("page.item_management.drawer.flag.item_activedis", { defaultValue: "Active Discount" })}
                                        checked={formData.item_activedis}
                                        onChange={(e) => updateBoolean("item_activedis", e.currentTarget.checked)}
                                    />
                                    <Checkbox
                                        label={t("page.item_management.drawer.flag.item_weighted", { defaultValue: "Weighted" })}
                                        checked={formData.item_weighted}
                                        onChange={(e) => updateBoolean("item_weighted", e.currentTarget.checked)}
                                    />
                                    <Checkbox
                                        label={t("page.item_management.drawer.flag.is_returnable", { defaultValue: "Returnable" })}
                                        checked={formData.is_returnable}
                                        onChange={(e) => updateBoolean("is_returnable", e.currentTarget.checked)}
                                    />
                                </Stack>
                            </Column>
                        </Columns>

                        <Box display="flex" justifyContent="flexEnd">
                            <Actions>
                                <Button type={isReadOnly ? "button" : "submit"} variant="ghost" tone="brandAccent">
                                    {isReadOnly
                                        ? t("page.item_management.drawer.submit.view_only", { defaultValue: "View Only" })
                                        : submitting
                                            ? t("page.item_management.drawer.submit.saving", { defaultValue: "Saving..." })
                                            : itemToEdit
                                                ? t("page.item_management.drawer.submit.update", { defaultValue: "Update Item" })
                                                : t("page.item_management.drawer.submit.create", { defaultValue: "Create Item" })}
                                </Button>
                            </Actions>
                        </Box>
                    </Stack>
                </Box>
            </form>
        </Drawer>
    );
};

export default CreateUpdateItemDrawer;
